import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TravelStats } from '../../types';
import { statsApi } from '../../api/stats.api';
import { countryName } from '../../utils/countries';
import Section from '../ui/Section';
import WorldMap from './WorldMap';

/** ADR-0045: lived trips, nights and countries, with the map painted. */
export default function TravelStatsSection() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState<TravelStats | null>(null);

  useEffect(() => {
    statsApi.travelStats().then(setStats).catch(() => setStats(null));
  }, []);

  if (!stats || stats.trips === 0) return null;

  const figures = [
    { key: 'trips', value: stats.trips, label: t('stats.trips', { count: stats.trips }) },
    { key: 'nights', value: stats.nights, label: t('stats.nights', { count: stats.nights }) },
    { key: 'countries', value: stats.countries.length, label: t('stats.countries', { count: stats.countries.length }) },
    { key: 'regions', value: stats.regions.length, label: t('stats.regions', { count: stats.regions.length }) },
  ];

  return (
    <Section title={t('stats.title')}>
      <div className="px-4 py-4 space-y-4">
        <div className="grid grid-cols-4 gap-2">
          {figures.map(f => (
            <div key={f.key}>
              <p className="font-serif text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums leading-none">{f.value}</p>
              <p className="font-mono text-[10px] tracking-[0.1em] uppercase text-slate-400 mt-1">{f.label}</p>
            </div>
          ))}
        </div>
        <WorldMap visited={stats.countries.map(c => c.code)} />
        <ul className="flex flex-wrap gap-1.5">
          {stats.countries.map(c => (
            <li key={c.code} className="inline-flex items-center gap-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full px-2 py-0.5">
              <span aria-hidden>{c.flag}</span>
              {countryName(c.code, i18n.language)}
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}
